// Stealth v2 in the ability lane (owner 2026-08-28, plans/ft3d-stealth-redesign-2026-08-27.md). PHP's copies were two sibling janus.php
// branches (→ ft_stealth_enter_hidden, stealth_awareness.php; → ft_stealth_reveal_after_action, stealth_reveal_on_action.php) that left with
// janus.php. Without them the browser ran Hide's retired contest, stripped the caster from its hostile Self area and committed no rows
// ("Invalid rows", FT3D playtest 2026-10-14), and every action of a hidden caster handed back.
//   · Hide ENTERS the Hidden stance. The catalog row still carries its retired 20 ft Sneak-vs-Attention contest (Self range, opposing-only
//     aoe, Hidden on contest-failed); observer contests belong to the 60 ft awareness zones the movement lanes judge. The selection is
//     ignored, so Hide starts no combat and names no observer.
//   · ACTING GIVES YOU AWAY (owner 2026-09-04 / 09-14): a hidden or invisible caster's attack, spell or hostile action ends the status, in
//     the same use, before its dispatch. The decision is the pure predicate stealthActionRevealsHidden (compile/stealth-reveal-on-action.ts).
import { type JanusCtx, type Row, activeStatuses, ecId, itemField, record, refuse, upper, definitionNotHeld, handBack } from './context.js';
import { type JanusInput } from './janus.js';
import { spendAbilityAction } from './templates/template-abilities-roll.js';
import { applyStatusEffect } from './statuses/apply-status-effect.js';
import { removeStoredStatus } from './statuses/status-effect-removal-helpers.js';
import { layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { statusItemId } from '../statuses/expiry.js';
import { stealthActionRevealsHidden } from '../compile/stealth-reveal-on-action.js';
import type { StealthRevealOnActionInput } from '../types.js';
import { addRevealedAttackCarry } from './stealth-reveal-carry.js';

export const HIDE_ABILITY = 'C7326C89-CD32-C8C8-2DA9-29D66FFC2284';
const SEARCH_FOR_HIDDEN_INVENTORY = '5B518ED7-4CEF-72BE-F20B-912598267714';
export const HIDDEN = '47667406-1B72-9D60-310B-D8CF6BFE397D';
const INVISIBLE = '84B91EEB-2FA6-D1CB-1347-75BFBDA2A6DD';
export const SNEAKING = '1BA5473E-6218-6688-1C68-DCA4FEF00A97';
const ATTACK_ROLL_TYPES: Readonly<Record<string, NonNullable<StealthRevealOnActionInput['rollType']>>> = {
  '2FE8DFB7-FE61-4862-AF71-3523262350A0': 'weapon_melee', '3F85CA97-F9B2-A83A-FDE8-E523FD99346C': 'weapon_ranged',
  '5740CA49-006E-A7A0-B46B-6DDED6CD546C': 'spell_melee', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C': 'spell_ranged',
};
const CONTESTING_ROLL = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
/** ft_stealth_non_hostile_contester_skill_ids(): a contest rolled only with these is looking or hiding, not hostile. */
const LOOKING_SKILLS: Readonly<Record<string, string>> = {
  'F99172CC-DDEF-48D4-87F8-8B94F0071247': 'attention', '28E0D03E-86B3-FDF4-E03A-8DFA1043FE3E': 'inspection',
  '64493E62-BCAF-27F1-2B6A-EC84DE328F66': 'sneak',
};
const RANGED_WEAPON_TYPE = '2BBC1E4E-5A42-41A4-8392-2C755D7A3C7C';
/** Engine re-entries janus() is handed with the ORIGINAL ability — not the creature acting (janus.php's $ft_reveal_is_new_action list). */
const REPLAY_SENTINELS = new Set(['moving_into_p_aoe', 'finish_casting', 'finish_casting_ritual_spell', 'finish_crafting_wip',
  'finish_gathering', 'finish_lockpicking', 'finish_reading', 'finish_training']);

const phpNonEmpty = (value: unknown): boolean => value !== null && value !== undefined && value !== false
  && value !== 0 && value !== '0' && value !== '' && (!Array.isArray(value) || value.length > 0);
const held = (ctx: JanusCtx, creature: Row, id: string): Row[] => activeStatuses(ctx, creature).filter((status) => statusItemId(status) === id);

export function isHide(input: JanusInput): boolean {
  return upper(input.abilityId) === HIDE_ABILITY;
}

/** ft_stealth_enter_hidden: one AP (free out of combat, none inside a trigger frame), then Hidden on the caster for the ability's own
 *  duration, under its own connection, logged `status_effect_applied` (the voice-emote type audio_helpers.php expects). Already hidden: PHP
 *  wrote nothing — a player's use with no rows is no commit, so it is refused by name; a trigger frame stays silent. */
export function enterHidden(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  if (held(ctx, caster, HIDDEN).length) {
    if (ctx.trigger) return;
    refuse('You are already hidden.');
  }
  spendAbilityAction(ctx, ability, caster);
  applyStatusEffect(ctx, input, ability, HIDDEN, 'status_effect_applied', caster, caster, undefined, true);
}

/** The predicate's input from the used item and its nested attack_ability_arr rider (PHP walks [$item_id_used_arr, $rider]). */
function revealInput(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): StealthRevealOnActionInput {
  const rider = record(ability.attack_ability_arr);
  const candidates = [ability, ...(rider && Object.keys(rider).length ? [rider] : [])];
  // a catalog weapon row carries its roll_type / weapon_type on its weapon_classification (PHP's compiled weapon has them folded in) —
  // a hidden Slam must read as the Weapon Melee Attack Roll it is
  const classification = (row: Row): Row | null => {
    const id = upper(ctx.read.value(row, 'weapon_classification') ?? '');
    return id ? ctx.read.definition(id) ?? null : null;
  };
  const layer = (row: Row, key: string): string => upper(ctx.read.value(row, key) ?? ctx.read.value(classification(row), key) ?? '');
  const rollTypeOf = (row: Row): string => layer(row, 'roll_type');
  const rollType = candidates.map((row) => ATTACK_ROLL_TYPES[rollTypeOf(row)]).find(Boolean) ?? null;
  // limb 4: the first hostile candidate — a direct damage roll, or a contest unless every declared contester is a looking skill
  let hostileRollType: StealthRevealOnActionInput['hostileRollType'] = null, contesterSkills: string[] = [];
  for (const row of candidates) {
    const type = rollTypeOf(row);
    if (type === DAMAGE_ROLL) { hostileRollType = 'damage'; contesterSkills = []; break; }
    if (type !== CONTESTING_ROLL) continue;
    const skills = [...new Set(ctx.read.entries(row, 'skill_roll_contester').map((entry) => upper(layerListEntryUuid(entry))).filter(Boolean))]
      .map((id) => LOOKING_SKILLS[id] ?? id);
    if (!skills.length || skills.some((skill) => !Object.values(LOOKING_SKILLS).includes(skill))) {
      hostileRollType = 'contesting'; contesterSkills = skills; break;
    }
  }
  return {
    isNewAction: !REPLAY_SENTINELS.has(String(input.useAbilityVariable1 ?? '')),
    rollType,
    itemType: String(itemField(ability, 'item_type') ?? ''),
    hasSpellLevel: candidates.some((row) => ctx.read.value(row, 'spell_level') != null),
    hasSpellCircle: candidates.some((row) => ctx.read.value(row, 'spell_circle') != null),
    weaponIsRanged: candidates.some((row) => layer(row, 'weapon_type') === RANGED_WEAPON_TYPE),
    casterExemptRangedReveal: phpNonEmpty(ctx.read.value(caster, 'ranged_attacks_do_not_reveal_position')),
    hostileRollType,
    contesterSkills,
  };
}

/** ft_stealth_reveal_after_action, before the action's dispatch (a trigger frame — an opportunity attack from concealment — reveals too).
 *  Quiet Breath's exemption keeps Hidden only: an exempt ranged attack still ends Invisible. Sneaking rides along with Hidden. The ended
 *  statuses' attack ADV / DIS is banked for the action's first attack roll (stealth-reveal-carry.ts). */
export function revealAfterAction(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void;
export function revealAfterAction(ctx: JanusCtx, input: null, ability: null, caster: Row, cause: 'spotted'): void;
export function revealAfterAction(ctx: JanusCtx, input: JanusInput | null, ability: Row | null, caster: Row, cause?: 'spotted'): void {
  // Spotting ends Hidden/Sneaking without ending Invisible or banking an attack bonus.
  const removeEvery = (id: string): void => {
    const tried = new Set<string>();
    for (;;) {
      const row = held(ctx, caster, id).find((status) => !tried.has(upper(status.status_id) || `${id}:${upper(status.status_connected_id)}`));
      if (!row) return;
      tried.add(upper(row.status_id) || `${id}:${upper(row.status_connected_id)}`);
      removeStoredStatus(ctx, caster, row, 'single');
    }
  };
  if (cause === 'spotted') { removeEvery(HIDDEN); removeEvery(SNEAKING); return; }
  if (!input || !ability) handBack('stealth:reveal-action-input-not-held');
  if ([HIDE_ABILITY, SEARCH_FOR_HIDDEN_INVENTORY].includes(upper(input.abilityId))) return;
  const hidden = held(ctx, caster, HIDDEN), invisible = held(ctx, caster, INVISIBLE);
  if (!hidden.length && !invisible.length) return;
  const verdict = stealthActionRevealsHidden(revealInput(ctx, input, ability, caster));
  const endsInvisible = invisible.length > 0 && (verdict.reveals || verdict.exempt);
  const endsHidden = hidden.length > 0 && verdict.reveals;
  const ending = [...(endsInvisible ? [INVISIBLE] : []), ...(endsHidden ? [HIDDEN] : [])];
  if (!ending.length) return;
  addRevealedAttackCarry(ctx, ecId(caster), ending.map((id) => {
    const definition = ctx.read.definition(id) ?? record((id === HIDDEN ? hidden : invisible)[0]!.status_arr);
    if (!definition) definitionNotHeld('stealth_reveal:definition-not-held', id);
    return { statusItemId: id, definition: definition! };
  }));
  // remove_status_effect_item_id_single_connection per item: every row of it, re-read before each removal — a row that shared a connection
  // with one removed before it may already be gone as its peer
  if (endsInvisible) removeEvery(INVISIBLE);
  if (!endsHidden) return;
  removeEvery(HIDDEN);
  removeEvery(SNEAKING);
}
